import React from 'react';
import {
  Typography,
  Box,
  styled,
  Link,
  LinkProps,
  useTheme,
  List,
  ListItem,
} from '@material-ui/core';

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import SleepioSighing from '@/assets/SleepioSighing.svg';
import { OnboardingParams } from '@/pages/Onboarding/types';
import { getProductEmail } from '@/utils/productHelpers';

const ContentContainer = styled(Box)({
  marginTop: 32,
  '& > *': {
    marginBottom: 32,
  },
});

const CrisisResourceContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  marginTop: 16,
  padding: 16,
  borderRadius: 12,
}));

const ResourceLink = (props: LinkProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ color: theme.palette.grey[800], display: 'inline-block' }}>
      <Link
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        color="inherit"
        style={{ wordWrap: 'break-word' }}
        {...props}
      />
    </Box>
  );
};

type CoverageRejectionProps = {
  isCaregiver: boolean;
};

export const SleepioCoverageRejection: React.FC<CoverageRejectionProps> = () => {
  const { t } = useTranslation();
  const { product } = useParams<OnboardingParams>();
  const productEmail = getProductEmail(product);
  return (
    <>
      <Typography variant="h2" style={{ marginBottom: 8 }}>
        {t('coverage.sleepio.rejection.main_title')}
      </Typography>
      <Typography>
        {t('coverage.sleepio.rejection.not_eligible')}
      </Typography>
      <Typography>
        {t('coverage.sleepio.rejection.contact')}
        {' '}
        <ResourceLink href={`mailto:${productEmail}`}>
          {productEmail}
        </ResourceLink>
      </Typography>
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <img src={SleepioSighing} alt="sleepio sighing" style={{ maxWidth: '240px' }} />
      </Box>
      <Box>
        <Typography variant="h2">
          {t('coverage.sleepio.rejection.emergency')}
        </Typography>
        <Typography>
          {t('coverage.sleepio.rejection.resource')}
        </Typography>
      </Box>
      <List dense style={{ listStyleType: 'disc', paddingLeft: 16 }}>
        <ListItem style={{ display: 'list-item' }}>
          <Typography>
            {t('coverage.sleepio.rejection.medical_or_mental')}
            {' '}
            <span style={{ fontWeight: 700 }}>{t('coverage.sleepio.rejection.dial_911')}</span>
            {' '}
            {t('coverage.sleepio.rejection.emergency_room')}
          </Typography>
        </ListItem>
        <ListItem style={{ display: 'list-item' }}>
          <Typography>{t('coverage.sleepio.rejection.health_questions')}</Typography>
        </ListItem>
        <ListItem style={{ display: 'list-item' }}>
          <Typography>{t('coverage.sleepio.rejection.crisis_support')}</Typography>
        </ListItem>
      </List>
      <CrisisResourceContainer>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.suicide_lifeline')}
        </Typography>
        <Typography>
          <ResourceLink href="tel:988">
            {t('coverage.sleepio.rejection.call')}
            {' '}
            988
          </ResourceLink>
        </Typography>
        <Typography>
          {t('coverage.sleepio.rejection.chat_online')}
          {' '}
          <ResourceLink href="www.988lifeline.org/chat/">www.988lifeline.org/chat/</ResourceLink>
        </Typography>
      </CrisisResourceContainer>
      <CrisisResourceContainer>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.crisis_text_label')}
        </Typography>
        <Typography>
          {t('coverage.sleepio.rejection.crisis_text')}
          {' '}
          <ResourceLink href="tel:741741">741741</ResourceLink>
        </Typography>
      </CrisisResourceContainer>
      <ContentContainer>
        <Typography variant="h2">
          {t('coverage.sleepio.rejection.additional_resources')}
        </Typography>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.services_administration')}
        </Typography>
        <Typography>
          <ResourceLink href="https://www.samhsa.gov/find-help/national-helpline">
            https://www.samhsa.gov/find-help/national-helpline
          </ResourceLink>
        </Typography>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.psychological_association')}
        </Typography>
        <Typography>
          <ResourceLink href="https://locator.apa.org/">
            https://locator.apa.org/
          </ResourceLink>
        </Typography>
        <Typography variant="body2">
          {t('coverage.sleepio.rejection.sleep_academy')}
        </Typography>
        <Typography>
          <ResourceLink href="https://sleepeducation.org/">
            https://sleepeducation.org/
          </ResourceLink>
        </Typography>
        <Typography>
          {t('coverage.sleepio.rejection.disclaimer')}
        </Typography>
      </ContentContainer>
    </>
  );
};
